// A live pattern cover (rendered as CSS). color: 'primary' | 'accent' | hex.
export interface HelpCover {
  pattern?: string
  icon?: string
  color?: string
}

export interface HelpCategoryNode {
  id: string
  slug: string
  name: string
  description: string | null
  icon: string | null
  parent_id: string | null
  position: number
  published: boolean
  section: string | null
  feature_image_url: string | null
  cover: HelpCover
}

export interface HelpArticleRow {
  id: string
  slug: string
  title: string
  summary: string | null
  help_category_id: string | null
  position: number
  tags: string[]
  published: boolean
  featured: boolean
  views_count: number
  helpfulness_score: number
  feature_image_url: string | null
  cover: HelpCover
  updated_at: string | null
}

export interface HelpArticleFull extends HelpArticleRow {
  content_json: Record<string, unknown>
  rendered_html: string | null
  seo_title: string | null
  seo_description: string | null
}

// Home-page layout template. 'classic' is the original centred layout; the
// others are richer chrome variants. Keep in sync with HelpCenterTemplate::KEYS.
export type HelpCenterTemplateKey = 'classic' | 'aurora' | 'atlas'

export interface HelpCenterTheme {
  primary_color?: string
  accent_color?: string
  appearance?: 'light' | 'dark' | 'system'
  template?: HelpCenterTemplateKey
  font?: string
  radius?: 'sharp' | 'default' | 'rounded'
  header_style?: 'neutral' | 'branded'
  hero_text?: 'light' | 'dark'
  // Hero background treatment (Aurora): vibrant mesh, soft single glow, or solid.
  hero_gradient?: 'mesh' | 'soft' | 'solid'
  // Structural colour overrides (theme studio). bg = outer canvas,
  // page = fixed-width centre band, surface = cards. Blank = use default
  // light/dark set.
  bg_color?: string
  bg_accent_color?: string
  page_color?: string
  surface_color?: string
  surface_hover_color?: string
  border_color?: string
  text_color?: string
  muted_color?: string
}

// The keys the theme studio paints/clears as a set.
export const HELP_SURFACE_KEYS = [
  'bg_color', 'bg_accent_color', 'page_color', 'surface_color',
  'surface_hover_color', 'border_color', 'text_color', 'muted_color',
] as const

export interface HelpFooterLink {
  label: string
  url: string
}

export interface HelpFooterColumn {
  title: string
  links: HelpFooterLink[]
}

export interface HelpCenterBranding {
  footer_text?: string
  // Subtitle under "Browse by category" on the Aurora home template; falls
  // back to the live collection count when blank.
  categories_subtitle?: string
  show_powered_by?: boolean
  footer_columns?: HelpFooterColumn[]
  // Custom links rendered in the top navigation (distinct from footer columns).
  nav_links?: HelpFooterLink[]
}

export interface HelpCenterChat {
  channel_id?: string | null
  position?: 'bottom-right' | 'bottom-left'
  launcher?: 'always' | 'button'
}

export interface HelpCenterSettings {
  hero_heading: string | null
  hero_subtext: string | null
  ga4_measurement_id: string | null
  chat: HelpCenterChat
  theme: HelpCenterTheme
  branding: HelpCenterBranding
  // Brand (product) colours — seed the palette generator / swatch fallbacks so a
  // new help center inherits the product brand, not the Banshee default.
  brand_primary_color: string | null
  brand_accent_color: string | null
  logo_url: string | null
  favicon_url: string | null
  hero_image_url: string | null
  og_image_url: string | null
  settings_version: number
}

// ---- Announcement banners (BC-142) ----------------------------------------

/** Which pages a banner appears on. 'tagged' matches any-of its tags against
 *  an article's tags; 'category' matches one category and its articles. */
export type HelpBannerScope = 'all' | 'tagged' | 'category'

/** Keep in sync with HelpBanner::TONES. 'brand' tints from the help center's
 *  own primary colour, so the default banner reads as part of the site. */
export type HelpBannerTone = 'brand' | 'neutral' | 'info' | 'success' | 'warning' | 'danger'

export interface HelpBannerRow {
  id: string
  title: string | null
  body_html: string | null
  scope: HelpBannerScope
  tags: string[]
  help_category_id: string | null
  tone: HelpBannerTone
  accent_color: string | null
  active: boolean
  dismissible: boolean
  starts_at: string | null
  ends_at: string | null
  position: number
}
